import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Seo from "../components/Seo";
import Dimension from "../components/Dimension";
import ThreeViewer from "../components/ThreeViewer";
import { currentLang } from "../i18n";
import { LT_TOOLS, LT_KPIS, LT_MODELS, LT_BRAND, toolDescription, toolTag } from "../data";

function Home() {
  const { t, i18n } = useTranslation();
  const [heroReady, setHeroReady] = useState(false);
  // Digit grouping follows the language: 4,067 in English, 4'067 in Swiss
  // German and Italian, 4 067 in French.
  const lang = currentLang(i18n);
  const num = new Intl.NumberFormat(lang === "en" ? "en" : `${lang}-CH`);

  // The hero is the general arrangement (the building); the row below carries
  // the two other scenes as details, so no label is repeated on the sheet.
  // A hover hint means nothing on a touch screen, so it is not rendered there.
  const canHover =
    typeof window !== "undefined" &&
    Boolean(window.matchMedia) &&
    window.matchMedia("(hover: hover)").matches;
  const details = [
    {
      model: LT_MODELS.bridge,
      aside: canHover ? t("home.hoverReveal") : undefined,
      revealColours: true,
    },
    { model: LT_MODELS.facade },
  ];

  return (
    <div className="container">
      <Seo page="home" path="/" />

      <section className="hero">
        <div>
          <h1>
            {t("home.heroTitle1")} {t("home.heroTitle2")}
            <br />
            {t("home.heroTitle3")} {t("home.heroTitle4")}
          </h1>
          <p className="hero-desc">{t("home.heroDesc")}</p>
          <div className="btn-row">
            <Link className="btn btn-solid" to="/solutions">
              {t("home.btnBrowsePlain")}
            </Link>
            <a
              className="btn"
              href={LT_BRAND.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              github.com/louistrue
            </a>
          </div>

          <dl className="meta-pairs">
            <div>
              <dt className="k">{t("home.metaFounder")}</dt>
              <dd className="v">{t("home.metaFounderVal")}</dd>
            </div>
            <div>
              <dt className="k">{t("home.metaBased")}</dt>
              <dd className="v">{t("home.metaBasedVal")}</dd>
            </div>
            <div>
              <dt className="k">{t("home.metaStandards")}</dt>
              <dd className="v">{t("home.metaStandardsVal")}</dd>
            </div>
            <div>
              <dt className="k">{t("home.metaStack")}</dt>
              <dd className="v">{t("home.metaStackVal")}</dd>
            </div>
          </dl>
        </div>

        <div className="hero-view">
          <div className="frame frame-hero">
            {!heroReady && <div className="loading">{t("home.loading")}</div>}
            <ThreeViewer
              modelPath={LT_MODELS.building.path}
              viewDir={LT_MODELS.building.viewDir}
              autoRotate
              onReady={() => setHeroReady(true)}
            />
          </div>
          <Dimension
            label={t(LT_MODELS.building.metaKey)}
            aside={t("home.dragToOrbit")}
          />
        </div>
      </section>

      {/* ---- Tool index ---- */}
      <div className="band">
        <Dimension label={t("dimension.tools", { n: LT_TOOLS.length })} />
      </div>
      <h2 className="sr-only">{t("home.section2Title")}</h2>
      <table className="schedule">
        <thead>
          <tr>
            <th>{t("home.tableHeaders.name")}</th>
            <th>{t("home.tableHeaders.area")}</th>
            <th className="hide-sm">{t("home.tableHeaders.description")}</th>
            <th className="c-num hide-sm">{t("home.tableHeaders.stars")}</th>
          </tr>
        </thead>
        <tbody>
          {LT_TOOLS.map((tool) => (
            <tr
              key={tool.key}
              className="linked"
              onClick={() =>
                tool.url && window.open(tool.url, "_blank", "noopener")
              }
            >
              <td className="c-name">
                <a
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {tool.title}
                </a>
              </td>
              <td className="c-area">{toolTag(t, tool)}</td>
              <td className="c-desc hide-sm">{toolDescription(t, tool)}</td>
              <td className="c-num hide-sm">
                {tool.closed
                  ? t("solutions.closed")
                  : tool.stars != null
                    ? tool.stars
                    : "–"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ---- Detail views ---- */}
      <div className="band">
        <Dimension label={t("dimension.detailViews", { n: details.length })} />
      </div>
      <h2 className="sr-only">{t("home.section1Title")}</h2>
      <div className="detail-row">
        {details.map((m) => (
          <div key={m.model.path} className="detail-view">
            <div className="frame frame-detail">
              <ThreeViewer
                modelPath={m.model.path}
                viewDir={m.model.viewDir}
                revealColours={m.revealColours}
              />
            </div>
            <h3>{t(m.model.titleKey)}</h3>
            <Dimension label={t(m.model.metaKey)} aside={m.aside} />
          </div>
        ))}
      </div>

      {/* ---- Legend ---- */}
      <div className="band">
        <Dimension label={t("dimension.metrics", { n: LT_KPIS.length })} />
      </div>
      <h2 className="sr-only">{t("home.legendTitle")}</h2>
      <div className="legend">
        {LT_KPIS.map((k) => (
          <div key={k.labelKey}>
            <div className="v">
              {typeof k.v === "number" ? num.format(k.v) : k.v}
            </div>
            <div className="l">{t(k.labelKey)}</div>
          </div>
        ))}
      </div>

      {/* ---- Contact line ---- */}
      <div className="band">
        <Dimension label={t("dimension.channels", { n: 2 })} />
      </div>
      <section className="closing band-bottom">
        <h2>{t("home.ctaTitle")}</h2>
        <div className="btn-row">
          <a className="btn btn-red" href={`mailto:${LT_BRAND.contactEmail}`}>
            {LT_BRAND.contactEmail}
          </a>
          <a
            className="btn"
            href={LT_BRAND.github}
            target="_blank"
            rel="noopener noreferrer"
          >
            github.com/louistrue
          </a>
        </div>
      </section>
    </div>
  );
}

export default Home;
